import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from '../styles/FeaturedListings.module.css';
import { withShopifyWidth } from '../utils/shopifyImage';
import { slugify } from '../data/catalog';
import SensitiveThumbnail from './SensitiveThumbnail';
import { getRecentlyViewed, clearRecentlyViewed } from '../lib/recentlyViewed';

// A diferencia de FeaturedListings (que pide /api/deals/featured), la lista
// en si se lee directo de localStorage -- sin llamada de red, sin
// consentimiento de cookies de marketing/analytics (ver el comentario
// extenso en lib/recentlyViewed.js): es la misma naturaleza que recordar el
// contenido del carrito, no rastreo con fines de marketing.
//
// SI hay una llamada de red aparte, deliberadamente pequeña y best-effort
// (/api/deals/prices): comprobar si el precio bajo desde que se vio cada
// producto, para avisarlo como gancho real de vuelta ("¡Bajo de precio!").
// Si falla o tarda, la seccion sigue funcionando igual que antes (con el
// precio guardado en el momento de la visita) -- nunca bloquea el render.
const RecentlyViewed = ({ excludeId }) => {
    const [items, setItems] = useState([]);
    const [currentPrices, setCurrentPrices] = useState({});
    const trackRef = useRef(null);

    useEffect(() => {
        setItems(getRecentlyViewed(excludeId));
    }, [excludeId]);

    useEffect(() => {
        if (items.length === 0) {
            return undefined;
        }
        let active = true;
        const controller = new AbortController();
        const ids = items.map((item) => item.id).join(',');

        fetch(`/api/deals/prices?ids=${encodeURIComponent(ids)}`, { signal: controller.signal })
            .then((response) => (response.ok ? response.json() : null))
            .then((data) => {
                if (active && data?.prices) {
                    setCurrentPrices(data.prices);
                }
            })
            .catch(() => {});

        return () => {
            active = false;
            controller.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items.map((item) => item.id).join(',')]);

    if (items.length === 0) {
        return null;
    }

    // Flechas finas, solo para dejar claro que se puede desplazar (en
    // escritorio no hay gesto tactil que lo insinue) -- se apoyan en el
    // scroll nativo ya existente, no sustituyen el swipe en movil.
    const scrollByCard = (direction) => {
        trackRef.current?.scrollBy({ left: direction * 236, behavior: 'smooth' });
    };

    const handleClear = () => {
        clearRecentlyViewed();
        setItems([]);
    };

    return (
        <section className={styles.section} aria-label="Visto recientemente">
            <div className={styles.carouselHeader}>
                <p className={styles.eyebrow}>Visto recientemente</p>
                <div className={styles.carouselArrows}>
                    <button type="button" className={styles.carouselClearButton} onClick={handleClear}>
                        Borrar historial
                    </button>
                    <button type="button" className={styles.carouselArrowButton} onClick={() => scrollByCard(-1)} aria-label="Ver anteriores">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M15 18l-6-6 6-6" />
                        </svg>
                    </button>
                    <button type="button" className={styles.carouselArrowButton} onClick={() => scrollByCard(1)} aria-label="Ver siguientes">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M9 18l6-6-6-6" />
                        </svg>
                    </button>
                </div>
            </div>
            <div className={styles.carouselTrack} ref={trackRef}>
                {items.map((item) => {
                    // Precio en vivo si llego a tiempo la respuesta de
                    // /api/deals/prices; si no, el guardado en el momento de
                    // la visita -- nunca se deja de mostrar un precio por un
                    // fallo/retraso de red.
                    const livePrice = currentPrices[String(item.id)];
                    const displayPrice = livePrice != null ? livePrice : Number(item.price);
                    const priceDropped = livePrice != null && livePrice < Number(item.price);
                    const discount = Number(item.oldPrice) > displayPrice && Number(item.oldPrice) > 0
                        ? Math.round(((Number(item.oldPrice) - displayPrice) / Number(item.oldPrice)) * 100)
                        : 0;
                    return (
                        <Link to={`/ofertas/${item.id}`} key={item.id} className={styles.card}>
                            <div className={styles.imageWrap}>
                                <SensitiveThumbnail
                                    isSensitive={slugify(item.category) === 'erotico-adultos'}
                                    src={withShopifyWidth(item.image, 350)}
                                    alt=""
                                    className={styles.image}
                                    loading="lazy"
                                    decoding="async"
                                />
                                {discount > 0 && (
                                    <span className={styles.discountBadge} aria-hidden="true">-{discount}%</span>
                                )}
                            </div>
                            <div className={styles.info}>
                                <span className={styles.store}>{item.store}</span>
                                <strong className={styles.name}>{item.name}</strong>
                                {priceDropped && (
                                    <span className={styles.priceDroppedBadge}>¡Bajó de precio!</span>
                                )}
                                <span className={styles.priceRow}>
                                    <span className={styles.price}>{displayPrice.toFixed(2)}€</span>
                                    {(priceDropped || Number(item.oldPrice || 0) > displayPrice) && (
                                        <span className={styles.originalPrice}>
                                            {(priceDropped ? Number(item.price) : Number(item.oldPrice)).toFixed(2)}€
                                        </span>
                                    )}
                                </span>
                            </div>
                        </Link>
                    );
                })}
            </div>
        </section>
    );
};

export default RecentlyViewed;
